import React, { useEffect, useState } from "react";
import * as LucideIcons from "lucide-react";
import { api } from "../lib/api";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../lib/auth";

function BadgeIcon({ name, className }) {
  const Icon = LucideIcons[name] || LucideIcons.Award;
  return <Icon className={className} />;
}

export default function Achievements() {
  const { user } = useAuth();
  const [badgesData, setBadgesData] = useState(null);
  const [rankData, setRankData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/me/badges").then(r => r.data),
      api.get("/me/rank").then(r => r.data),
    ])
      .then(([b, r]) => { setBadgesData(b); setRankData(r); })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-fc-navy flex" data-surface="light" data-testid="achievements-page">
      <Sidebar />
      <main className="flex-1 min-w-0 bg-[#F8F9FB] text-gray-900">
        <div className="h-20 px-6 lg:px-10 flex items-center justify-between border-b border-gray-200 bg-white/70 backdrop-blur sticky top-0 z-30">
          <div>
            <p className="fc-overline text-[10px]">ACHIEVEMENTS</p>
            <h1 className="text-lg sm:text-xl font-black tracking-tight">Your XP, ranks, and badges</h1>
          </div>
        </div>

        <div className="px-6 lg:px-10 py-8 space-y-12">
          {loading ? <p className="text-gray-500">Loading…</p> : (
            <>
              {/* Ranks */}
              <section data-testid="ranks-section">
                <h2 className="fc-h-section text-2xl mb-6">Ranks</h2>
                {rankData && (
                  <div className="bg-white border border-gray-200 p-6">
                    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                      <div>
                        <p className="fc-overline text-[10px] mb-2">CURRENT RANK</p>
                        <p className="text-4xl font-black tracking-tight text-fc-red">{rankData.current.name}</p>
                        <p className="text-gray-500 text-sm mt-1">{rankData.total_xp.toLocaleString()} XP</p>
                      </div>
                      {rankData.next && (
                        <div className="text-right">
                          <p className="fc-overline text-[10px] mb-2">NEXT</p>
                          <p className="text-xl font-black">{rankData.next.name}</p>
                          <p className="text-gray-500 text-sm">{rankData.xp_to_next.toLocaleString()} XP to go</p>
                        </div>
                      )}
                    </div>
                    {rankData.next && (
                      <div className="h-2 w-full bg-white/8 overflow-hidden rounded-full mb-8">
                        <div
                          className="h-full bg-fc-red transition-all duration-700"
                          style={{ width: `${Math.min(100, Math.max(2, (rankData.total_xp - rankData.current.min_xp) / (rankData.next.min_xp - rankData.current.min_xp) * 100))}%` }}
                          data-testid="rank-progress-bar"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <p className="fc-overline text-[10px] mb-3">UPCOMING</p>
                      {rankData.upcoming.length === 0 ? (
                        <p className="text-gray-500 text-sm">You've reached the top rank. 🏆</p>
                      ) : (
                        rankData.upcoming.map((r) => (
                          <div key={r.name} className="flex items-center justify-between border-t border-gray-100 py-3 text-sm" data-testid={`rank-row-${r.name}`}>
                            <span className="font-bold">{r.name}</span>
                            <span className="text-gray-500">{r.min_xp.toLocaleString()} XP required</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </section>

              {/* Badges */}
              <section data-testid="badges-section">
                <h2 className="fc-h-section text-2xl mb-6">Badges</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {badgesData?.badges?.map((b) => (
                    <div
                      key={b.id}
                      className={`bg-white border ${b.earned ? "border-fc-red/40" : "border-gray-200 opacity-60"} p-5 flex flex-col items-center text-center`}
                      data-testid={`badge-${b.slug}`}
                    >
                      <div className={`w-14 h-14 mb-3 flex items-center justify-center ${b.earned ? "bg-fc-red/15 text-fc-red" : "bg-white/5 text-gray-400"}`}>
                        <BadgeIcon name={b.icon} className="w-7 h-7" />
                      </div>
                      <p className="font-black text-sm mb-1">{b.name}</p>
                      <p className="text-[11px] text-gray-500 leading-snug">{b.description}</p>
                      {b.earned && b.awarded_at && (
                        <p className="text-[10px] text-fc-red mt-2 font-bold tracking-wider">{new Date(b.awarded_at).toLocaleDateString()}</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
